import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { flowRight as compose } from 'lodash'
import { withRouter } from 'react-router-dom'
import { graphql, withApollo } from 'react-apollo'
import getAllProducts from '../../graphql/getAllProducts'
import { Box } from '../../components/box'
import { Text } from '../../components/text'
import { Loader } from '../../components/loader'
import { Container, Row, Col } from '../../components/grid'
import { SearchInput, Selector } from '../../components/search-bar'
import { isSMB } from '../../utils/cardFilters'
import { trackImpression } from '../../utils/analytics'
import clientData from '../../utils/client-data'
import { Message } from '@paypalcorp/worldready-react'
import { ResultsHeader } from './ResultsHeader'
import { FilteredItemList, CardItem, BundleItem } from './FilteredItemList'

const elmo = clientData.experimentation

const TYPE_ALL = 'all'
const TYPE_CARDS = 'cards'
const TYPE_BUNDLES = 'bundles'
const TYPE_SMBS = 'businesses'
const ALL_TYPES = [TYPE_ALL, TYPE_CARDS, TYPE_BUNDLES, TYPE_SMBS]

const BUNDLES_CATEGORY = 'Gift Bundles'
const MIN_BUNDLES_CATEGORY = 'Mini Bundles'

const stripQuery = (value) =>
  value.replace(/[.,/#!$%^*;:{}=\-_`~()'"]/g, '').trim()

const Results = (props) => {
  const { data, basepath, match, location, history } = props

  const [resultsType, setResultsType] = useState(TYPE_ALL)
  const [query, setQuery] = useState('')
  const [exactSearch, setExactSearch] = useState(false)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const foundMatch = ALL_TYPES.indexOf(match.params.type) >= 0

    setResultsType(foundMatch ? match.params.type : TYPE_ALL)
    setQuery(decodeURIComponent(queryParams.get('q') || ''))
    setExactSearch(!!queryParams.get('e'))

    window.scrollTo(0, 0)
  }, [match, location])

  const handleSearchTypeUpdate = (value) => {
    history.replace(`/search/${value}${location.search}`)
  }

  const handleSearchSubmit = (value) => {
    const urlQuery =
      value && value.length > 0 ? `?q=${encodeURIComponent(value)}` : ''
    history.push(`/search/${TYPE_ALL}${urlQuery}`)
  }

  const SearchRow = styled(Row)`
    position: sticky;
    top: 64px;
    align-self: flex-start;
    z-index: 989;
    background: white;
  `

  const searchComponents = (
    <SearchRow pt="2" pb="1.5rem">
      <Col xs="12" md="6" lg="8">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          height="100%"
        >
          <SearchInput value={query} onSubmit={handleSearchSubmit} />
        </Box>
      </Col>
      {elmo.v2bundles ? (
        <Col xs="12" md="6" lg="4" pt={{ _: 2, md: 0 }} display="flex">
          <Box
            height="100%"
            width="100%"
            display="flex"
            flexDirection="row"
            justifyContent={{ _: 'flex-start', md: 'flex-end' }}
          >
            <Selector
              label="Filter results by type"
              onChange={handleSearchTypeUpdate}
              value={resultsType}
              options={ALL_TYPES.filter((type) => type !== TYPE_SMBS)}
            />
          </Box>
        </Col>
      ) : null}
    </SearchRow>
  )

  if (data.loading) {
    return (
      <Container>
        <Box pb="4">{searchComponents}</Box>
        <Loader py="100px" />
      </Container>
    )
  }

  const queryTokens = stripQuery(query)
    .split(/\s/)
    .filter((token) => token.length > 0)

  const getQueryScore = (string) => {
    const matchingTokens = queryTokens.filter(
      (token) => string.toLowerCase().indexOf(token.toLowerCase()) >= 0,
    )
    return matchingTokens.length / queryTokens.length
  }

  const scoreSku = (sku) => {
    if (exactSearch) return getQueryScore(sku.name)

    let matches = getQueryScore(sku.name) + getQueryScore(sku.keywords)
    sku.categories.forEach((category) => {
      matches += getQueryScore(category)
    })
    return matches
  }

  const { all_a_z } = data.getAllProducts

  let filteredCards = all_a_z.filter(
    (sku) =>
      sku.categories.indexOf(BUNDLES_CATEGORY) < 0 &&
      sku.categories.indexOf(MIN_BUNDLES_CATEGORY) < 0,
  )
  let filteredBundles = all_a_z.filter(
    (sku) =>
      sku.categories.indexOf(BUNDLES_CATEGORY) >= 0 ||
      sku.categories.indexOf(MIN_BUNDLES_CATEGORY) >= 0,
  )
  let filteredSMBs = all_a_z.filter(isSMB)

  if (queryTokens.length) {
    filteredCards = filteredCards
      .filter((card) => scoreSku(card) > 0)
      .sort((a, b) => scoreSku(a) - scoreSku(b))

    filteredBundles = filteredBundles
      .filter((bundle) => scoreSku(bundle) > 0)
      .sort((a, b) => scoreSku(a) - scoreSku(b))

    filteredSMBs = filteredSMBs
      .filter((smb) => scoreSku(smb) > 0)
      .sort((a, b) => scoreSku(a) - scoreSku(b))

    if (query.length >= 2) {
      trackImpression(
        'main:consumer:gifts:search:',
        `main:consumer:gifts:search:${resultsType}:::`,
        { srch: query },
      )
    }
  }

  if (!elmo.v2bundles) filteredBundles = []

  let resultsCount = filteredCards.length + filteredBundles.length
  if (resultsType === TYPE_CARDS) resultsCount = filteredCards.length
  if (resultsType === TYPE_BUNDLES) resultsCount = filteredBundles.length
  if (resultsType === TYPE_SMBS) resultsCount = filteredSMBs.length

  return (
    <Container>
      {searchComponents}

      {!!query && (
        <ResultsHeader>
          <Text>
            <Message
              id="pages/search.search.countString"
              count={resultsCount}
              query={query}
            />
          </Text>
        </ResultsHeader>
      )}

      <FilteredItemList
        basepath={basepath}
        show={resultsType === TYPE_ALL || resultsType === TYPE_CARDS}
        filteredItems={filteredCards}
        headerMessageID="pages/search.search.cardsHeader"
        Item={CardItem}
      />

      <FilteredItemList
        basepath={basepath}
        show={resultsType === TYPE_SMBS}
        filteredItems={filteredSMBs}
        headerMessageID="pages/search.search.cardsHeader"
        Item={CardItem}
      />

      <FilteredItemList
        show={resultsType === TYPE_ALL || resultsType === TYPE_BUNDLES}
        filteredItems={filteredBundles}
        headerMessageID="pages/search.search.bundlesHeader"
        Item={BundleItem}
      />
    </Container>
  )
}

Results.propTypes = {
  data: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.object,
    getAllProducts: PropTypes.object,
  }).isRequired,
  basepath: PropTypes.string,
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
}

const SearchResults = compose(
  withApollo,
  graphql(getAllProducts),
)(withRouter(Results))

export default SearchResults
